import { Card } from '@material-ui/core'
import dayjs from 'dayjs'
import i18n from 'i18next'
import { sumBy } from 'lodash'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import api from 'src/api'
import { PolicyAmount } from 'src/api/gen'
import { getJurisdictionTimezone, stringToZonedDateTime } from 'src/commons/temporalHelpers'
import { DeleteButton } from 'src/components/Button'
import { useProviders } from 'src/layout/hooks'

import { CurrencyIntegerField, IntegerField } from '../Editor/fields'

import { CardSection } from './Editor'
import { PeriodField, PoliciesField, ProviderField } from './Editor/fields'
import PenaltySchemeAccordion from './PenaltySchemesAccordion'

import { IPenaltyScheme } from '.'

interface IPenaltySchemeListProps {
  archiveScheme: () => void
  penaltyScheme: IPenaltyScheme
  policyAmounts: Map<string, PolicyAmount[]>
}

export const PenaltySchemeList = function (props: IPenaltySchemeListProps) {
  const { t } = useTranslation()
  const timezone = getJurisdictionTimezone()
  const { allProviders, providersMapping: providerMapping } = useProviders()

  async function fetchExportData(providerId: string) {
    await api.policyAmounts.policyAmountsExport({
      penalty_scheme: props.penaltyScheme.id,
      provider: providerId,
      // Export dates in the timezone and language of the user
      tz: timezone,
      lang: i18n.language,
    })
  }

  async function fetchExportPdfData(providerId: string) {
    await api.policyAmounts.policyAmountsExportPdf({
      penalty_scheme: props.penaltyScheme.id,
      provider: providerId,
      // Export dates in the timezone and language of the user
      tz: timezone,
      lang: i18n.language,
      start_time: dayjs(props.penaltyScheme.start_date).toLocaleString(),
      end_time: dayjs(props.penaltyScheme.end_date).toLocaleString(),
    })
  }

  const getProviderOnClickExport = function (providerId: string) {
    return async function () {
      await fetchExportData(providerId)
    }
  }

  const getProviderOnPDFClickExport = function (providerId: string) {
    return async function () {
      await fetchExportPdfData(providerId)
    }
  }

  const flatAmounts = useMemo(() => Array.from(props.policyAmounts.values()).flat(), [props.policyAmounts])
  const total_offenses_count = useMemo(() => {
    return sumBy(flatAmounts, (policyAmount: PolicyAmount) => policyAmount.offenses_count)
  }, [flatAmounts])

  const total_amount = useMemo(() => {
    return sumBy(flatAmounts, (policyAmount: PolicyAmount) => policyAmount.amount)
  }, [flatAmounts])

  return (
    <>
      <Card style={{ overflow: 'visible' }}>
        <CardSection
          title={t('titles.penalty_scheme.list.penalties')}
          xs={2}
          button={<DeleteButton label={t('buttons.reset')} onClick={props.archiveScheme} />}
        >
          <ProviderField
            title={t('titles.providers')}
            handleChange={console.error}
            validation={{ isFieldValid: () => true, getFieldErrorMsg: () => '' } as any}
            disabled
            providers={allProviders}
            value={props.penaltyScheme.providers}
            withSelectAll={false}
            oneLineSelect={false}
          />
          <PoliciesField
            title={t('titles.policies')}
            handleChange={console.error}
            validation={{ isFieldValid: () => true, getFieldErrorMsg: () => '' } as any}
            disabled
            allPolicies={props.penaltyScheme.policies_names}
            value={props.penaltyScheme.policies_names.map((policy) => policy.id)}
            withSelectAll={false}
            oneLineSelect={false}
          />
          <IntegerField
            title={t('titles.penalties_count')}
            handleChange={console.error}
            value={total_offenses_count}
            disabled
          />
          <CurrencyIntegerField title={t('titles.amount')} handleChange={console.error} value={total_amount} disabled />
          <PeriodField
            title={t('titles.time_range')}
            value={{
              from: stringToZonedDateTime(props.penaltyScheme.start_date),
              to: stringToZonedDateTime(props.penaltyScheme.end_date),
            }}
            handleChange={console.error}
            validation={{ isFieldValid: () => true, getFieldErrorMsg: () => '' } as any}
            disabled
          />
        </CardSection>
      </Card>
      {Array.from(props.policyAmounts.entries())
        .filter(([providerId]) => providerMapping.has(providerId)) // Ensure we display providers on which we have info
        .map(([providerId, policyAmounts]) => (
          <PenaltySchemeAccordion
            info={{
              amount: sumBy(policyAmounts, (policyAmount) => policyAmount.amount),
              offenses_count: sumBy(policyAmounts, (policyAmount) => policyAmount.offenses_count),
              provider: providerMapping.get(providerId)!,
              data: policyAmounts,
            }}
            key={providerId}
            onClickExport={getProviderOnClickExport(providerId)}
            onClickExportPdf={getProviderOnPDFClickExport(providerId)}
            penaltyScheme={props.penaltyScheme}
          />
        ))}
    </>
  )
}

export default PenaltySchemeList
